import { isValid, parse, parseISO } from 'date-fns';
import { calculateAge } from './dateUtils';
import Papa from 'papaparse';
import JSONParser from './jsonParser.ts';
import { decodeExcelXmlEscapes } from './textUtils';

export const processJSONFile = async (file, type, currentCompetition) => {
  const text = await file.text();
  const data = JSON.parse(text);

  switch (type) {
    case FileType.JSON.USAG_SANCTION_ATHLETES:
      return processUSAGSanctionAthletesJSON(data, currentCompetition);
    case FileType.JSON.USAG_SANCTION_CLUBS:
      return processUSAGSanctionClubsJSON(data);
    case FileType.JSON.USAG_SANCTION_COACHES:
      return processUSAGSanctionCoachesJSON(data);
    case FileType.JSON.USAG_SANCTION_JUDGES:
      return processUSAGSanctionJudgesJSON(data);
    case FileType.JSON.USAG_SANCTION_INDIVIDUALS:
      return processUSAGSanctionIndividualsJSON(data);
    case FileType.JSON.COMPETITION:
      return processCompetitionJSON(file);
    default:
      throw new Error('Unsupported JSON type');
  }
};

export const processCSVFile = async (file, type) => {
  const text = await file.text();
  const lines = text.split('\n').filter(line => line.trim());

  switch (type) {
    case FileType.CSV.USAG_SANCTION_ATHLETES:
      return processUSAGSanctionAthletes(lines);
    case FileType.CSV.USAG_SANCTION_CLUBS:
      return processUSAGSanctionClubs(lines);
    case FileType.CSV.COMPETITION:
      return processCompetitionCSV(lines);
    default:
      throw new Error('Unsupported CSV type');
  }
};

export const resolveFileType = (typeString) => {
  if (!typeString) return '';

  if (typeString.startsWith('CSV.')) {
    const subType = typeString.split('.')[1];
    return FileType.CSV[subType] || typeString;
  }
  return typeString;
};

export const FileType = {
  PDF: 'PDF',
  CSV: {
    COMPETITION: 'CSV.COMPETITION',
    USAG_SANCTION_ATHLETES: 'CSV.USAG_SANCTION_ATHLETES',
    USAG_SANCTION_CLUBS: 'CSV.USAG_SANCTION_CLUBS'
  },
  JSON: {
    COMPETITION: 'JSON.COMPETITION',
    USAG_SANCTION_ATHLETES: 'JSON.USAG_SANCTION_ATHLETES',
    USAG_SANCTION_CLUBS: 'JSON.USAG_SANCTION_CLUBS',
    USAG_SANCTION_COACHES: 'JSON.USAG_SANCTION_COACHES',
    USAG_SANCTION_JUDGES: 'JSON.USAG_SANCTION_JUDGES',
    USAG_SANCTION_INDIVIDUALS: 'JSON.USAG_SANCTION_INDIVIDUALS'
  },
  PTF: 'PTF',
  UNKNOWN: 'UNKNOWN'
};

export const validateFile = async (file) => {
  // Basic validation based on file extension and mime type
  const fileType = await getFileType(file);

  switch (fileType) {
    case FileType.PDF:
      return validatePDF(file);
    case FileType.CSV.COMPETITION:
      return validateCSV(file);
    case FileType.CSV.USAG_SANCTION_ATHLETES:
      return validateUSAGSanctionAthletes(file);
    case FileType.CSV.USAG_SANCTION_CLUBS:
      return validateUSAGSanctionClubs(file);
    case FileType.JSON.COMPETITION:
      return validateJSON(file);
    case FileType.JSON.USAG_SANCTION_ATHLETES:
      const athletesData = await file.text();
      return validateUSAGSanctionAthletesJSON(JSON.parse(athletesData));
    case FileType.JSON.USAG_SANCTION_CLUBS:
      const clubsData = await file.text();
      return validateUSAGSanctionClubsJSON(JSON.parse(clubsData));
    case FileType.JSON.USAG_SANCTION_COACHES:
      const coachesData = await file.text();
      return validateUSAGSanctionCoachesJSON(JSON.parse(coachesData));
    case FileType.JSON.USAG_SANCTION_JUDGES:
      const judgesData = await file.text();
      return validateUSAGSanctionJudgesJSON(JSON.parse(judgesData));
    case FileType.JSON.USAG_SANCTION_INDIVIDUALS:
      const individualsData = await file.text();
      return validateUSAGSanctionIndividualsJSON(JSON.parse(individualsData));
    case FileType.PTF:
      return validatePTF(file);
    default:
      return {
        isValid: false,
        error: 'Unsupported file format. Please upload a PDF, CSV, JSON, or PTF file.'
      };
  }
};

const getFileType = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  const mimeType = file.type;

  if (extension === 'pdf' && mimeType === 'application/pdf') {
    return FileType.PDF;
  } else if (extension === 'csv' && mimeType === 'text/csv') {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
      return FileType.CSV.COMPETITION;
    }

    // Try to detect USAG sanction athlete files first
    const firstLine = lines[0].replace(/['"]/g, '');
    if (firstLine.match(/^Sanction #\d+ Athlete Export/)) {
      return FileType.CSV.USAG_SANCTION_ATHLETES;
    }

    // Then try to detect club exports by checking headers
    if (lines.length > 1) {
      const headers = lines[0].split(',').map(h => h.replace(/['"]/g, ''));
      if (headers.includes('ClubID') && headers.includes('Club') && headers.includes('ClubAbbrev')) {
        return FileType.CSV.USAG_SANCTION_CLUBS;
      }
    }

    // Fall back to COMPETITION CSV
    return FileType.CSV.COMPETITION;
  } else if (extension === 'json' && mimeType === 'application/json') {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Check for USAG Sanction JSON structure
      if (data.status && data.data?.reservations && Array.isArray(data.data.reservations)) {
        const reservations = data.data.reservations;
        
        // Check filename patterns for USAG files (even when empty)
        const filename = file.name.toLowerCase();
        if (reservations.length === 0) {
          // Detect USAG file types from filename when reservations array is empty
          if (filename.includes('athlete')) return FileType.JSON.USAG_SANCTION_ATHLETES;
          if (filename.includes('coach')) return FileType.JSON.USAG_SANCTION_COACHES;
          if (filename.includes('judge')) return FileType.JSON.USAG_SANCTION_JUDGES;
          if (filename.includes('individual')) return FileType.JSON.USAG_SANCTION_INDIVIDUALS;
          if (filename.includes('club')) return FileType.JSON.USAG_SANCTION_CLUBS;
          // Fall back to competition if no pattern matches
          return FileType.JSON.COMPETITION;
        }

        // Check for club-specific fields
        if (reservations[0] && 'ClubID' in reservations[0] && 'ClubName' in reservations[0] && 'ClubAbbrev' in reservations[0]) {
          return FileType.JSON.USAG_SANCTION_CLUBS;
        }

        // Count different types of records
        const types = reservations.reduce((acc, item) => {
          if (item.MemberType === 'ATHL') acc.athletes++;
          else if (item.MemberType === 'PRO' && item.Level === 'Coach') acc.coaches++;
          else if (item.MemberType === 'PRO' && item.Level === 'Judge') acc.judges++;
          return acc;
        }, { athletes: 0, coaches: 0, judges: 0 });

        // Determine file type based on content
        if (types.athletes > 0 && types.coaches === 0 && types.judges === 0) {
          return FileType.JSON.USAG_SANCTION_ATHLETES;
        } else if (types.coaches > 0 && types.athletes === 0 && types.judges === 0) {
          return FileType.JSON.USAG_SANCTION_COACHES;
        } else if (types.judges > 0 && types.athletes === 0 && types.coaches === 0) {
          return FileType.JSON.USAG_SANCTION_JUDGES;
        } else if (types.athletes > 0 || types.coaches > 0 || types.judges > 0) {
          return FileType.JSON.USAG_SANCTION_INDIVIDUALS;
        }
      }

      return FileType.JSON.COMPETITION;
    } catch (error) {
      return FileType.JSON.COMPETITION;
    }
  } else if (extension === 'ptf') {
    return FileType.PTF;
  }

  return FileType.UNKNOWN;
};

const validatePDF = async (file) => {
  try {
    // Read the first few bytes to verify PDF signature
    const buffer = await readFileHeader(file, 5);
    const signature = new TextDecoder().decode(buffer);

    if (signature.startsWith('%PDF-')) {
      return {
        isValid: true,
        type: FileType.PDF
      };
    }

    return {
      isValid: false,
      error: 'Invalid PDF file format'
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating PDF file'
    };
  }
};

const validateUSAGSanctionAthletes = async (file) => {
  try {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    // Check for minimum required lines
    if (lines.length < 3) { // Title, headers, and at least one data row
      return {
        isValid: false,
        error: 'CSV file must contain title, headers and data rows'
      };
    }

    // Validate sanction export header
    const titleLine = lines[0].replace(/['"]/g, '');
    if (!titleLine.match(/^Sanction #\d+ Athlete Export - Created \d{2}\/\d{2}\/\d{4}$/)) {
      return {
        isValid: false,
        error: 'Invalid CSV format - missing or invalid sanction export header'
      };
    }

    // Validate column headers
    const expectedHeaders = [
      'AthleteID', 'First', 'Last', 'MemberType', 'InternationalMember',
      'ClubID', 'Club', 'ClubAbbrev', 'InternationalClub', 'ClubCity',
      'ClubState', 'ClubCountry', 'Sex', 'Hometown', 'State', 'Country',
      'HighSchool', 'ExpectedGraduation', 'Citizen', 'Birth', 'TodayAge',
      'SanctionEndAge', 'ProgramAgeGroupAge', 'SafeSportCompletion',
      'Level', 'AgeGroup'
    ];

    const headers = lines[1].split(',').map(h => h.replace(/['"]/g, ''));
    if (!expectedHeaders.every((header, i) => headers[i] === header)) {
      return {
        isValid: false,
        error: 'Invalid CSV format - incorrect column headers'
      };
    }

    // Validate at least one data row exists and has correct number of columns
    const dataRow = lines[2].split(',');
    if (dataRow.length !== expectedHeaders.length) {
      return {
        isValid: false,
        error: 'Invalid CSV format - incorrect number of data columns'
      };
    }

    return {
      isValid: true,
      type: FileType.CSV.USAG_SANCTION_ATHLETES
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating CSV file'
    };
  }
};

const processUSAGSanctionAthletes = (lines) => {
  // Extract sanction number and date from title
  const titleLine = lines[0].replace(/['"]/g, '');
  const titleMatch = titleLine.match(/^Sanction #(\d+) Athlete Export - Created (\d{2}\/\d{2}\/\d{4})$/);
  const sanctionNumber = titleMatch ? titleMatch[1] : '';
  const exportDate = titleMatch ? titleMatch[2] : '';
  const csvContent = lines.slice(titleMatch ? 1 : 0).join('\n');

  // Parse CSV using Papa Parse
  const { data } = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim()
  });

  // console.log(data)

  // Map the parsed data to our structure
  const athletes = data.map(athlete => ({
    id: athlete.AthleteID,
    usagNumber: athlete.AthleteID,
    firstName: athlete.First,
    lastName: athlete.Last,
    memberType: athlete.MemberType,
    isInternational: athlete.InternationalMember === 'Yes',
    clubId: athlete.ClubID,
    club: {
      id: athlete.ClubID,
      name: athlete.Club,
      abbreviation: athlete.ClubAbbrev,
      isInternational: athlete.InternationalClub === 'Yes',
      city: athlete.ClubCity,
      state: athlete.ClubState,
      country: athlete.ClubCountry
    },
    gender: athlete.Sex,
    hometown: athlete.Hometown,
    state: athlete.State,
    country: athlete.Country,
    highSchool: athlete.HighSchool,
    expectedGraduation: athlete.ExpectedGraduation,
    isCitizen: athlete.Citizen === 'Yes',
    birthDate: athlete.Birth,
    age: {
      current: parseInt(athlete.TodayAge) || 0,
      endOfSanction: parseInt(athlete.SanctionEndAge) || 0,
      programAgeGroup: parseInt(athlete.ProgramAgeGroupAge) || 0
    },
    safeSportCompleted: athlete.SafeSportCompletion || null,
    level: athlete.Level,
    ageGroup: athlete.AgeGroup
  }));

  // Group athletes by level and age group
  const athletesByLevel = athletes.reduce((acc, athlete) => {
    const level = athlete.level;
    const ageGroup = athlete.ageGroup;
    if (!acc[level]) acc[level] = {};
    if (!acc[level][ageGroup]) acc[level][ageGroup] = [];
    acc[level][ageGroup].push(athlete);
    return acc;
  }, {});

  // Get unique levels and age groups
  const levels = [...new Set(athletes.map(a => a.level))].filter(Boolean).map(level => ({
    id: level,
    name: level,
  }));

  const ageGroups = [...new Set(athletes.map(a => a.ageGroup))].filter(Boolean).map(group => ({
    id: group,
    name: group
  }));

  return {
    type: FileType.CSV.USAG_SANCTION_ATHLETES,
    competition: {
      name: ``,
      id: sanctionNumber,
      sanctionId: sanctionNumber,
      sanctionOrg: 'USAG',
      exportDate,
      type: 'USAG',
      startDate: '', // These would need to be set by the user
      endDate: '',   // These would need to be set by the user
      facility: '',  // These would need to be set by the user
      director: 'Wayne Thompson',
      status: 'Pending',
      website: '',
      details: '',
      colors: {
        primary: '',
        secondary: ''
      }
    },
    gymnasts: athletes,
    levels,
    ageGroups,
    athletesByLevel,
    statistics: {
      totalAthletes: athletes && new Set(athletes.map(a => a.id)).size,
      totalLevels: levels && new Set(athletes.map(a => a.level)).size,
      totalAgeGroups: ageGroups.length && new Set(athletes.map(a => a.ageGroup)).size,
      totalClubs: new Set(athletes.map(a => a.teamId)).size,
    }
  };
};

const processUSAGSanctionClubs = (lines) => {
  const csvContent = lines.join('\n');

  // Parse CSV using Papa Parse
  const { data } = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim()
  });

  // Map the parsed data to our structure
  const clubs = data.map(row => ({
    clubId: row.ClubID,
    name: row.Club,
    abbreviation: row.ClubAbbrev,
    isInternational: row.InternationalClub.toLowerCase() === 'yes',
    city: row.City,
    state: row.State,
    country: row.Country,
    mailingAddress1: row.MailingAddress1,
    mailingAddress2: row.MailingAddress2,
    mailingCity: row.MailingCity,
    mailingState: row.MailingState,
    mailingZip: row.MailingZip,
    mailingCountry: row.MailingCountry,
    email: row.Email,
    phone: row.Phone,
    fax: row.Fax,
    ownerId: row.OwnerID,
    ownerName: row.Owner,
    contactName: row.ClubContact,
    // Use array indices for duplicate fields
    contactPhone: row['Phone.1'],
    contactEmail: row['Email.1'],
    meetContactName: row.MeetContact,
    meetContactPhone: row['Phone.2'],
    meetContactEmail: row['Email.2'],
    athleteCount: parseInt(row.Athletes) || 0,
    coachCount: parseInt(row.Coaches) || 0
  }));

  return {
    type: FileType.CSV.USAG_SANCTION_CLUBS,
    teams: clubs,
    statistics: {
      totalClubs: clubs.length,
      totalAthletes: clubs.reduce((sum, club) => sum + club.athleteCount, 0),
      totalCoaches: clubs.reduce((sum, club) => sum + club.coachCount, 0),
      stateBreakdown: clubs.reduce((acc, club) => {
        if (club.state) {
          acc[club.state] = (acc[club.state] || 0) + 1;
        }
        return acc;
      }, {}),
      totalStates: clubs.reduce((states, club) =>
        club.state ? states.add(club.state) : states, new Set()).size,
      statesList: [...new Set(clubs.map(club => club.state).filter(Boolean))].sort().join(', ')
    }
  };
};

const processCompetitionCSV = (lines) => {
  const headers = lines[0].split(',').map(h => h.replace(/['"]/g, ''));
  const data = lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.replace(/['"]/g, ''));
    return headers.reduce((obj, header, index) => {
      obj[header] = values[index] || '';
      return obj;
    }, {});
  });

  return {
    type: FileType.CSV.COMPETITION,
    headers,
    data,
    statistics: {
      totalRows: data.length,
      totalColumns: headers.length
    }
  };
};

const validateCSV = async (file) => {
  try {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      return {
        isValid: false,
        error: 'CSV file must contain header and data rows'
      };
    }

    // Check all rows have same number of columns
    const columnCount = lines[0].split(',').length;
    const hasValidColumns = lines.every(line => line.split(',').length === columnCount);

    if (!hasValidColumns) {
      return {
        isValid: false,
        error: 'CSV file has inconsistent number of columns'
      };
    }

    return {
      isValid: true,
      type: FileType.CSV.COMPETITION
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating CSV file'
    };
  }
};

const validateUSAGSanctionClubs = async (file) => {
  try {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    // Check for minimum required lines
    if (lines.length < 3) { // Title, headers, and at least one data row
      return {
        isValid: false,
        error: 'CSV file must contain title, headers and data rows'
      };
    }

    // Validate column headers
    const expectedHeaders = [
      'ClubID', 'Club', 'ClubAbbrev', 'InternationalClub', 'City',
      'State', 'Country', 'MailingAddress1', 'MailingAddress2', 'MailingCity',
      'MailingState', 'MailingZip', 'MailingCountry', 'Email', 'Phone',
      'Fax', 'OwnerID', 'Owner', 'ClubContact', 'Phone', 'Email',
      'MeetContact', 'Phone', 'Email', 'Athletes', 'Coaches'
    ];

    const headers = lines[0].split(',').map(h => h.replace(/['"]/g, ''));
    if (!expectedHeaders.every((header, i) => headers[i] === header)) {
      return {
        isValid: false,
        error: 'Invalid CSV format - incorrect column headers'
      };
    }

    // Validate at least one data row exists and has correct number of columns
    const dataRow = lines[2].split(',');
    if (dataRow.length !== expectedHeaders.length) {
      return {
        isValid: false,
        error: 'Invalid CSV format - incorrect number of data columns'
      };
    }

    return {
      isValid: true,
      type: FileType.CSV.USAG_SANCTION_CLUBS
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating CSV file'
    };
  }
};

const validateJSON = async (file) => {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    // Check if it's a USAG sanction athletes JSON
    if (data.athletes && Array.isArray(data.athletes)) {
      return validateUSAGSanctionAthletesJSON(data);
    }

    // Check if it's a USAG sanction clubs JSON
    if (data.clubs && Array.isArray(data.clubs)) {
      return validateUSAGSanctionClubsJSON(data);
    }

    return {
      isValid: true,
      type: FileType.JSON.COMPETITION
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Invalid JSON format'
    };
  }
};

const validateUSAGSanctionAthletesJSON = (data) => {
  try {
    // Validate required top-level structure
    if (!data.status || !data.data || !data.data.reservations || !Array.isArray(data.data.reservations)) {
      return {
        isValid: false,
        error: 'Invalid JSON format - missing required structure: status and data.reservations array'
      };
    }

    // Required fields for each athlete in reservations
    const requiredFields = [
      'MemberID', 'FirstName', 'LastName', 'DOB',
      'Discipline', 'MemberType', 'Status',
      'Level', 'LevelCode', 'OrgID',
      'ClubAbbrev', 'ClubName', 'InternationalClub'
    ];

    // Validate each athlete has required fields
    const invalidAthlete = data.data.reservations.find(athlete =>
      !requiredFields.every(field => {
        const hasField = athlete.hasOwnProperty(field);
        // Allow null/empty for some fields but they must exist
        return hasField;
      })
    );

    if (invalidAthlete) {
      return {
        isValid: false,
        error: 'Invalid athlete data - missing required fields'
      };
    }

    // Validate status is "success"
    if (data.status !== 'success') {
      return {
        isValid: false,
        error: 'Invalid status - must be "success"'
      };
    }

    return {
      isValid: true,
      type: FileType.JSON.USAG_SANCTION_ATHLETES
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating USAG Sanction Athletes JSON'
    };
  }
};

const processUSAGSanctionAthletesJSON = (data, currentCompetition) => {
  if (!data?.data?.reservations) {
    console.error("Missing data.data.reservations:", data);
    throw new Error("Invalid data structure - missing reservations array");
  }

  // First validate against USAGAthleteReservation interface
  const reservations = data.data.reservations.map((athlete, index) => {
    return {
      OrgID: athlete.OrgID,
      ClubAbbrev: athlete.ClubAbbrev,
      ClubName: athlete.ClubName,
      InternationalClub: athlete.InternationalClub ? "1" : "0",
      MemberID: athlete.MemberID,
      LastName: athlete.LastName,
      FirstName: athlete.FirstName,
      DOB: athlete.DOB,
      Discipline: athlete.Discipline,
      MemberType: athlete.MemberType,
      InternationalMember: athlete.InternationalMember ? 1 : 0,
      Status: athlete.Status,
      RegDate: athlete.RegDate,
      Apparatus: athlete.Apparatus || "ALL",
      Level: athlete.Level,
      LevelCode: athlete.LevelCode,
      AgeGroup: athlete.AgeGroup,
      Scratched: athlete.Scratched ? 1 : 0,
      ScratchDate: athlete.ScratchDate,
      ModifiedDate: athlete.ModifiedDate,
      ProgramAgeGroupAge: athlete.ProgramAgeGroupAge || ""
    };
  });

  // Track latest modified date across reservations
  const latestModified = reservations
    .map(r => r.ModifiedDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t))
    .reduce((max, t) => Math.max(max, t), 0);
  const metadata = latestModified ? { usagLastModified: new Date(latestModified).toISOString() } : {};

  // Then map to Gymnast interface
  const athletes = reservations.map((athlete, index) => {
    // Calculate age using the athlete's DOB and current competition start date
    const age = calculateAge(
      athlete.DOB,
      null, // No session date available in USAG data
      currentCompetition?.competition
    );

    return ({
      id: athlete.MemberID,
      firstName: athlete.FirstName,
      lastName: athlete.LastName,
      teamId: athlete.OrgID,
      level: athlete.Level,
      ageGroup: athlete.AgeGroup || athlete.ageGroup || undefined,
      dateOfBirth: athlete.DOB,
      age, // Add calculated age
      usagId: athlete.MemberID,
      gender: athlete.Discipline === "Men" ? "M" : athlete.Discipline === "Women" ? "F" : "O",
      status: athlete.Status.toLowerCase() === "active" ? "active" : "inactive",
      events: athlete.Apparatus === "ALL" ? [] : athlete.Apparatus.split(","),
      scratched: !!athlete.Scratched,
      notes: athlete.Scratched ? `Scratched on ${athlete.ScratchDate}` : undefined,
      membershipExpiry: undefined, // Not provided in USAG data
      programAgeGroupAge: athlete.ProgramAgeGroupAge || athlete.programAgeGroupAge || undefined,
      _debug_original: athlete, // Temporary debug field to see full original data
      _debug_mapping: { // Additional debug info for age group mapping
        rawAgeGroup: athlete.AgeGroup,
        rawAgeGroupLower: athlete.ageGroup,
        rawProgramAge: athlete.ProgramAgeGroupAge,
        rawProgramAgeLower: athlete.programAgeGroupAge
      }
    });
  });
  
  // Group athletes by level and discipline
  const athletesByLevel = athletes.reduce((acc, athlete) => {
    const level = athlete.level;
    const discipline = athlete.discipline;
    if (!acc[level]) acc[level] = {};
    if (!acc[level][discipline]) acc[level][discipline] = [];
    acc[level][discipline].push(athlete);
    return acc;
  }, {});

  // Get unique levels and disciplines
  const levels = [...new Set(athletes.map(a => a.level))].filter(Boolean).map(level => ({
    id: level,
    name: level,
  }));

  const disciplines = [...new Set(athletes.map(a => a.discipline))].filter(Boolean).map(disc => ({
    id: disc,
    name: disc
  }));

  // Group athletes by level for statistics
  const gymnastsByLevel = athletes.reduce((acc, athlete) => {
    const level = athlete.level;
    if (!acc[level]) acc[level] = 0;
    acc[level]++;
    return acc;
  }, {});

  // Group athletes by age group for statistics
  const gymnastsByAgeGroup = athletes.reduce((acc, athlete) => {
    const ageGroup = athlete.ageGroup || 'Unknown';
    if (!acc[ageGroup]) acc[ageGroup] = 0;
    acc[ageGroup]++;
    return acc;
  }, {});

  return {
    type: FileType.JSON.USAG_SANCTION_ATHLETES,
    gymnasts: athletes,
    levels,
    disciplines,
    athletesByLevel,
    statistics: {
      totalGymnasts: athletes.length,
      totalScratches: athletes.length - athletes.filter(a => !a.scratched).length,
      totalLevels: levels.length,
      totalDisciplines: disciplines.length,
      totalClubs: new Set(athletes.map(a => a.teamId)).size,
      gymnastsByLevel,
      gymnastsByAgeGroup,
      totalAgeGroups: Object.keys(gymnastsByAgeGroup).length
    },
    metadata
  };
};

const processUSAGSanctionClubsJSON = (data) => {
  // Extract clubs from reservations array and map to our Team interface
  const clubs = data.data.reservations.map(club => ({
    id: club.ClubID,
    name: club.ClubName,
    shortName: club.ClubAbbrev || club.ClubName,
    city: club.ClubCity || '',
    state: club.ClubState || '',
    country: club.ClubCountry || 'USA',
    region: club.Region || '',
    address: club.MailingAddress1 ? 
      `${club.MailingAddress1}${club.MailingAddress2 ? ', ' + club.MailingAddress2 : ''}` : '',
    contact: club.ClubContactName || '',
    phone: club.ClubContactPhone || '',
    email: club.ClubContactEmail || '',
    website: club.Website || '',
    memberNumber: club.ClubID?.toString() || '',
    status: club.Status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
    type: 'club',
    coaches: [], // Will be populated when coaches are imported
    contacts: {
      club: {
        name: club.ClubContactName || '',
        email: club.ClubContactEmail || '',
        phone: club.ClubContactPhone || ''
      },
      meet: {
        name: club.MeetContactName || '',
        email: club.MeetContactEmail || '',
        phone: club.MeetContactPhone || ''
      }
    }
  }));

  // Track latest modified date across clubs
  const latestModified = data.data.reservations
    .map(c => c.ModifiedDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t))
    .reduce((max, t) => Math.max(max, t), 0);
  const metadata = latestModified ? { usagLastModified: new Date(latestModified).toISOString() } : {};

  return {
    type: FileType.JSON.USAG_SANCTION_CLUBS,
    teams: clubs,
    statistics: {
      totalClubs: clubs.length,
      stateBreakdown: clubs.reduce((acc, club) => {
        if (club.state) {
          acc[club.state] = (acc[club.state] || 0) + 1;
        }
        return acc;
      }, {}),
      totalStates: clubs.reduce((states, club) =>
        club.state ? states.add(club.state) : states, new Set()).size,
      statesList: [...new Set(clubs.map(club => club.state).filter(Boolean))].sort().join(', ')
    },
    metadata
  };
};

const validateSessions = (sessions) => {
  if (!Array.isArray(sessions)) {
    throw new Error("Sessions must be an array");
  }

  sessions.forEach((session, index) => {
    // Required fields validation
    const requiredFields = [
      'id',
      'sessionNumber',
      'name',
      'gender',
      'timeBlocks',
      'levels'
    ];

    requiredFields.forEach(field => {
      if (!session.hasOwnProperty(field)) {
        throw new Error(`Session at index ${index} is missing required field: ${field}`);
      }
    });

    // Validate timeBlocks structure
    if (typeof session.timeBlocks !== 'object') {
      throw new Error(`Session ${session.sessionNumber}: timeBlocks must be an object`);
    }

    // Validate levels is an array
    if (!Array.isArray(session.levels)) {
      throw new Error(`Session ${session.sessionNumber}: levels must be an array`);
    }

    // Validate gender value
    if (session.gender && !['M', 'F', 'B'].includes(session.gender)) {
      throw new Error(`Session ${session.sessionNumber}: invalid gender value`);
    }

    // Validate session number is positive
    if (typeof session.sessionNumber !== 'number' || session.sessionNumber <= 0) {
      throw new Error(`Session ${session.sessionNumber}: sessionNumber must be a positive number`);
    }
  });
};

const processCompetitionJSON = async (file) => {
  JSONParser.fromFile(file)
    .then(parser => {
      const data = parser.parse();
      const { competition, sessions, gymnasts } = data;

      // Error checking
      if (!competition || Object.keys(competition).length === 0) {
        throw new Error("Failed to parse competition information");
      }

      // Validate sessions
      try {
        validateSessions(sessions);
      } catch (error) {
        console.error("Session validation failed:", error.message);
        throw error;
      }
      if (!gymnasts || gymnasts.length === 0) {
        console.warn("No gymnasts found in the parsed data");
      }

      // Additional error checking for new fields
      if (!data.coaches || data.coaches.length === 0) {
        console.warn("No coaches found in the parsed data");
      }
      if (!data.ageGroups || data.ageGroups.length === 0) {
        console.warn("No age groups found in the parsed data");
      }
      if (!data.levels || data.levels.length === 0) {
        console.warn("No levels found in the parsed data");
      }

      return {
        type: FileType.JSON.COMPETITION,
        data,
        statistics: {
          totalFields: Object.keys(data).length,
          hasNestedObjects: Object.values(data).some(v => typeof v === 'object' && v !== null)
        }
      };
    }
  )
};

const validateUSAGSanctionCoachesJSON = (data) => {
  try {
    if (!data.status || !data.data || !data.data.reservations || !Array.isArray(data.data.reservations)) {
      return {
        isValid: false,
        error: 'Invalid JSON format - missing required structure: status and data.reservations array'
      };
    }

    // Allow empty arrays
    if (data.data.reservations.length === 0) {
      return {
        isValid: true,
        type: FileType.JSON.USAG_SANCTION_COACHES
      };
    }

    const requiredFields = [
      'MemberID', 'FirstName', 'LastName', 'MemberType',
      'Discipline', 'Status', 'OrgID', 'ClubName',
      'ClubAbbrev', 'InternationalClub', 'RegDate',
      'ModifiedDate'
    ];

    const invalidCoach = data.data.reservations.find(coach =>
      !requiredFields.every(field => coach.hasOwnProperty(field))
    );

    if (invalidCoach) {
      return {
        isValid: false,
        error: 'Invalid coach data - missing required fields'
      };
    }

    if (data.status !== 'success') {
      return {
        isValid: false,
        error: 'Invalid status - must be "success"'
      };
    }

    return {
      isValid: true,
      type: FileType.JSON.USAG_SANCTION_COACHES
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating USAG Sanction Coaches JSON'
    };
  }
};

const validateUSAGSanctionJudgesJSON = (data) => {
  try {
    if (!data.status || !data.data || !data.data.reservations || !Array.isArray(data.data.reservations)) {
      return {
        isValid: false,
        error: 'Invalid JSON format - missing required structure: status and data.reservations array'
      };
    }

    // Allow empty arrays
    if (data.data.reservations.length === 0) {
      return {
        isValid: true,
        type: FileType.JSON.USAG_SANCTION_JUDGES
      };
    }

    const requiredFields = [
      'MemberID', 'FirstName', 'LastName', 'MemberType',
      'Discipline', 'Status', 'OrgID', 'ClubName',
      'ClubAbbrev', 'InternationalClub', 'RegDate',
      'ModifiedDate'
    ];

    const invalidJudge = data.data.reservations.find(judge =>
      !requiredFields.every(field => judge.hasOwnProperty(field))
    );

    if (invalidJudge) {
      return {
        isValid: false,
        error: 'Invalid judge data - missing required fields'
      };
    }

    if (data.status !== 'success') {
      return {
        isValid: false,
        error: 'Invalid status - must be "success"'
      };
    }

    return {
      isValid: true,
      type: FileType.JSON.USAG_SANCTION_JUDGES
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating USAG Sanction Judges JSON'
    };
  }
};

const validateUSAGSanctionIndividualsJSON = (data) => {
  try {
    if (!data.status || !data.data || !data.data.reservations || !Array.isArray(data.data.reservations)) {
      return {
        isValid: false,
        error: 'Invalid JSON format - missing required structure: status and data.reservations array'
      };
    }

    // Allow empty arrays (e.g., Judge.json with no judges registered)
    if (data.data.reservations.length === 0) {
      return {
        isValid: true,
        type: FileType.JSON.USAG_SANCTION_INDIVIDUALS
      };
    }

    // USAG's "individual" endpoint returns BOTH athletes (ATHL) and coaches/staff (PRO)
    // This is the comprehensive endpoint with all registered individuals
    // Validate that records have the standard USAG reservation structure
    const standardFields = [
      'MemberID', 'FirstName', 'LastName', 'MemberType',
      'Discipline', 'Status', 'OrgID', 'ClubName',
      'ClubAbbrev', 'InternationalClub', 'RegDate',
      'ModifiedDate'
    ];

    // Check if all records have the standard USAG fields
    const invalidRecord = data.data.reservations.find(record =>
      !standardFields.every(field => record.hasOwnProperty(field))
    );

    if (invalidRecord) {
      return {
        isValid: false,
        error: 'Invalid individual data - missing required fields'
      };
    }

    if (data.status !== 'success') {
      return {
        isValid: false,
        error: 'Invalid status - must be "success"'
      };
    }

    return {
      isValid: true,
      type: FileType.JSON.USAG_SANCTION_INDIVIDUALS
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating USAG Sanction Individuals JSON'
    };
  }
};

const processUSAGSanctionCoachesJSON = (data) => {
  // Track latest modified date across reservations
  const latestModified = data.data.reservations
    .map(coach => coach.ModifiedDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t))
    .reduce((max, t) => Math.max(max, t), 0);
  const metadata = latestModified ? { usagLastModified: new Date(latestModified).toISOString() } : {};

  const coaches = data.data.reservations.map(coach => ({
    id: coach.MemberID,
    usagNumber: coach.MemberID,
    firstName: coach.FirstName,
    lastName: coach.LastName,
    memberType: coach.MemberType,
    discipline: coach.Discipline,
    status: coach.Status,
    club: {
      id: coach.OrgID,
      name: coach.ClubName,
      abbreviation: coach.ClubAbbrev,
      isInternational: coach.InternationalClub === "1"
    },
    registration: {
      date: coach.RegDate,
      modifiedDate: coach.ModifiedDate,
      scratched: coach.Scratched === 1,
      scratchDate: coach.ScratchDate
    }
  }));

  // Group coaches by club
  const coachesByClub = coaches.reduce((acc, coach) => {
    const clubId = coach.club.id;
    if (!acc[clubId]) {
      acc[clubId] = {
        clubName: coach.club.name,
        coaches: []
      };
    }
    acc[clubId].coaches.push(coach);
    return acc;
  }, {});

  return {
    type: FileType.JSON.USAG_SANCTION_COACHES,
    coaches,
    coachesByClub,
    statistics: {
      totalCoaches: coaches.length,
      activeCoaches: coaches.filter(c => !c.registration.scratched).length,
      scratchedCoaches: coaches.filter(c => c.registration.scratched).length,
      totalClubs: Object.keys(coachesByClub).length,
      coachesPerClub: Object.entries(coachesByClub).reduce((acc, [clubId, data]) => {
        acc[data.clubName] = data.coaches.length;
        return acc;
      }, {}),
      memberTypeBreakdown: coaches.reduce((acc, coach) => {
        acc[coach.memberType] = (acc[coach.memberType] || 0) + 1;
        return acc;
      }, {})
    },
    metadata
  };
};

const processUSAGSanctionJudgesJSON = (data) => {
  // Track latest modified date across reservations
  const latestModified = data.data.reservations
    .map(judge => judge.ModifiedDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t))
    .reduce((max, t) => Math.max(max, t), 0);
  const metadata = latestModified ? { usagLastModified: new Date(latestModified).toISOString() } : {};

  const judges = data.data.reservations.map(judge => ({
    id: judge.MemberID,
    usagNumber: judge.MemberID,
    firstName: judge.FirstName,
    lastName: judge.LastName,
    memberType: judge.MemberType,
    level: judge.Level,
    discipline: judge.Discipline,
    status: judge.Status,
    club: {
      id: judge.OrgID,
      name: judge.ClubName,
      abbreviation: judge.ClubAbbrev,
      isInternational: judge.InternationalClub === "1"
    },
    registration: {
      date: judge.RegDate,
      modifiedDate: judge.ModifiedDate,
      scratched: judge.Scratched === 1,
      scratchDate: judge.ScratchDate
    },
    role: 'Judge'
  }));

  return {
    type: FileType.JSON.USAG_SANCTION_JUDGES,
    judges,
    statistics: {
      totalJudges: judges.length,
      activeJudges: judges.filter(j => !j.registration.scratched).length,
      scratchedJudges: judges.filter(j => j.registration.scratched).length
    },
    metadata
  };
};

const processUSAGSanctionIndividualsJSON = (data, currentCompetition) => {
  // USAG's "individual" endpoint returns ALL individuals (athletes + coaches/staff)
  // Separate them by MemberType
  const reservations = data.data.reservations;
  
  // Track latest modified date from feed for display
  const latestModified = reservations
    .map(r => r.ModifiedDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(t => !Number.isNaN(t))
    .reduce((max, t) => Math.max(max, t), 0);
  const metadata = latestModified ? { usagLastModified: new Date(latestModified).toISOString() } : {};
  
  const athletes = reservations.filter(r => r.MemberType === 'ATHL');
  const coaches = reservations.filter(r => r.MemberType === 'PRO' && r.Level === 'Coach');
  const judges = reservations.filter(r => r.MemberType === 'PRO' && r.Level === 'Judge');
  const other = reservations.filter(r => r.MemberType === 'PRO' && r.Level !== 'Coach' && r.Level !== 'Judge');
  
  // Process athletes using athlete processor
  const athleteData = athletes.length > 0 
    ? processUSAGSanctionAthletesJSON({ status: 'success', data: { reservations: athletes } }, currentCompetition)
    : { gymnasts: [], levels: [], disciplines: [], statistics: { totalGymnasts: 0 } };
  
  // Process coaches using coach processor
  const coachData = coaches.length > 0
    ? processUSAGSanctionCoachesJSON({ status: 'success', data: { reservations: coaches } })
    : { coaches: [], statistics: { totalCoaches: 0 } };
  
  // Process judges
  const judgeList = judges.map(judge => ({
    id: judge.MemberID,
    firstName: judge.FirstName,
    lastName: judge.LastName,
    memberType: judge.MemberType,
    role: 'Judge',
    discipline: judge.Discipline,
    status: judge.Status,
    club: {
      id: judge.OrgID,
      name: judge.ClubName,
      abbreviation: judge.ClubAbbrev
    },
    registration: {
      date: judge.RegDate,
      modifiedDate: judge.ModifiedDate,
      scratched: judge.Scratched === 1,
      scratchDate: judge.ScratchDate
    }
  }));
  
  // Process other PRO members (staff, etc.)
  const individuals = other.map(individual => ({
    id: individual.MemberID,
    firstName: individual.FirstName,
    lastName: individual.LastName,
    memberType: individual.MemberType,
    role: individual.Level || 'Staff',
    discipline: individual.Discipline,
    status: individual.Status,
    club: {
      id: individual.OrgID,
      name: individual.ClubName,
      abbreviation: individual.ClubAbbrev
    },
    registration: {
      date: individual.RegDate,
      modifiedDate: individual.ModifiedDate,
      scratched: individual.Scratched === 1,
      scratchDate: individual.ScratchDate
    }
  }));
  
  // Return combined data
  return {
    type: FileType.JSON.USAG_SANCTION_INDIVIDUALS,
    // Include all data types
    gymnasts: athleteData.gymnasts || [],
    coaches: coachData.coaches || [],
    judges: judgeList,
    individuals: individuals,
    // Preserve athlete-specific data
    levels: athleteData.levels || [],
    disciplines: athleteData.disciplines || [],
    // Combined statistics (include all fields needed by reducer)
    statistics: {
      totalIndividuals: reservations.length,
      totalGymnasts: athletes.length,
      totalCoaches: coaches.length,
      totalJudges: judges.length,
      totalOther: other.length,
      totalLevels: athleteData.levels?.length || 0,
      totalAgeGroups: athleteData.statistics?.totalAgeGroups || 0,
      totalClubs: new Set(reservations.map(r => r.OrgID)).size,
      totalTeams: new Set(reservations.map(r => r.OrgID)).size,
      // Include athlete-specific statistics from athleteData
      totalScratches: athleteData.statistics?.totalScratches || 0,
      gymnastsByLevel: athleteData.statistics?.gymnastsByLevel || {},
      gymnastsByAgeGroup: athleteData.statistics?.gymnastsByAgeGroup || {},
      memberTypeBreakdown: {
        athletes: athletes.length,
        coaches: coaches.length,
        judges: judges.length,
        other: other.length
      }
    },
    metadata
  };
};

const processUSAGSanctionIndividualsJSON_OLD = (data, currentCompetition) => {
  // OLD VERSION - Keep for reference if needed
  const individuals = data.data.reservations.map(individual => ({
    id: individual.IndividualID,
    firstName: individual.FirstName,
    lastName: individual.LastName,
    role: individual.Role,
    certifications: individual.Certifications,
    status: individual.Status,
    contact: {
      email: individual.Email,
      phone: individual.Phone
    },
    assignments: individual.Assignments || [],
    notes: individual.Notes || ''
  }));

  // Group individuals by role
  const individualsByRole = individuals.reduce((acc, individual) => {
    const role = individual.role;
    if (!acc[role]) acc[role] = [];
    acc[role].push(individual);
    return acc;
  }, {});

  return {
    type: FileType.JSON.USAG_SANCTION_INDIVIDUALS,
    individuals,
    individualsByRole,
    statistics: {
      totalIndividuals: individuals.length,
      roleBreakdown: Object.entries(individualsByRole).reduce((acc, [role, items]) => {
        acc[role] = items.length;
        return acc;
      }, {}),
      totalRoles: Object.keys(individualsByRole).length
    }
  };
};

const validateUSAGSanctionClubsJSON = (data) => {
  try {
    // Validate required top-level structure
    if (!data.status || !data.data || !data.data.reservations || !Array.isArray(data.data.reservations)) {
      return {
        isValid: false,
        error: 'Invalid JSON format - missing required structure: status and data.reservations array'
      };
    }

    // Required fields for each club in reservations
    const requiredFields = [
      'ClubID', 'ClubAbbrev', 'ClubName', 'ClubCity', 'ClubState',
      'ClubContactName', 'ClubContactEmail', 'ClubContactPhone',
      'MeetContactName', 'MeetContactEmail', 'MeetContactPhone',
      'InternationalClub'
    ];

    // Validate each club has required fields
    const invalidClub = data.data.reservations.find(club =>
      !requiredFields.every(field => {
        const hasField = club.hasOwnProperty(field);
        // Allow null/empty for some fields but they must exist
        return hasField;
      })
    );

    if (invalidClub) {
      return {
        isValid: false,
        error: 'Invalid club data - missing required fields'
      };
    }

    // Validate status is "success"
    if (data.status !== 'success') {
      return {
        isValid: false,
        error: 'Invalid status - must be "success"'
      };
    }

    return {
      isValid: true,
      type: FileType.JSON.USAG_SANCTION_CLUBS
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating USAG Sanction Clubs JSON'
    };
  }
};


const validatePTF = async (file) => {
  try {
    const text = await file.text();
    const lines = text.split('\n');

    // Check for ProScore version header
    if (!lines[0].includes('[ProScore]') && !lines[1].includes('Version=')) {
      return {
        isValid: false,
        error: 'Invalid PTF file format - missing ProScore version header'
      };
    }

    // Validate sections exist
    const requiredSections = ['Meets', 'Events', 'Sessions', 'Gymnasts'];
    const content = text.toLowerCase();
    const missingSections = requiredSections.filter(section =>
      !content.includes(`[${section.toLowerCase()}]`)
    );

    if (missingSections.length > 0) {
      return {
        isValid: false,
        error: `Invalid PTF file format - missing required sections: ${missingSections.join(', ')}`
      };
    }

    // Check for basic data structure
    const hasValidStructure = lines.some(line =>
      line.includes('=') || // Has key-value pairs
      /^\[.*\]$/.test(line.trim()) // Has section headers
    );

    if (!hasValidStructure) {
      return {
        isValid: false,
        error: 'Invalid PTF file format - missing required data structure'
      };
    }

    return {
      isValid: true,
      type: FileType.PTF
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating PTF file'
    };
  }
};

const readFileHeader = (file, bytes) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const blob = file.slice(0, bytes);

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);

    reader.readAsArrayBuffer(blob);
  });
};
